import { LicenseManager } from '@ag-grid-enterprise/core'
import { i18n } from '@/i18n';
import germanLocale from '@/components/CX/Grid/locale.de.js';

export default {
  props: {
    clientId:           { type: Number, default: () => NaN },
    colorRange:         { type: Array,  default: () => [] },
    groupDisplayType:   { type: String, default: () => null }, // 'groupRows', null
    tooltipShowDelay:   { type: Number, default: 500 },
    theme:              { type: String, default: () => 'light' },
    alternativeDisplay: { type: Boolean, default: () => false },
    waves:              { type: Array,  default: () => [] },
    wavesSelected:      { type: Array,  default: () => [] }
  },

  data() {
    return {
      localRowData: [], 
      localStaticColumnDefinitions: [],
      localDynamicColumnDefinitions: [],
      localeText: null,

      gridApi:  undefined,
      gridColumnApi: undefined,

      themes: {
        options: [ {
          "value": "ag-theme-alpine",
          "text": "ag-theme-alpine"
        }, {
          "value": "ag-theme-alpine-dark",
          "text": "ag-theme-alpine-dark"
        }, {
          "value": "ag-theme-balham",
          "text": "ag-theme-balham"
        }, {
          "value": "ag-theme-balham-dark",
          "text": "ag-theme-balham-dark"
        }, {
          "value": "ag-theme-material",
          "text": "ag-theme-material"
        }],
        selected: "ag-theme-alpine"
      },
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              contractColumnSelection: true,
              suppressColumnFilter: false,
              suppressColumnExpandAll: false,
              suppressColumnSelectAll: false,
              suppressPivotMode: true,
              suppressPivots: false,
              suppressRowGroups: true,
              suppressValues: true,
            },
            width: 300,
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            width: 400,
          }
        ],
        defaultToolPanel: 'columns'
      },
      excelStyles: [
        {
          id: 'indent-1',
          alignment: { indent: 1 },
          dataType: 'string',
        },
        {
          id: 'indent-2',
          alignment: { indent: 2 },
          dataType: 'string',
        },
        {
          id: 'indent-3',
          alignment: { indent: 3 },
          dataType: 'string',
        },
      ],
    }
  },
  
  beforeMount() {
    LicenseManager.setLicenseKey(process.env.VUE_APP_AGGRID);
    this.localeText = i18n.locale == 'de-DE' ? germanLocale : null;
  },

  computed: {
    dynamicColumnDefs() {
      return [
          ...this.localStaticColumnDefinitions, 
          ...this.localDynamicColumnDefinitions];
    },
    getTheme() {
      return this.theme === 'light' 
        ? this.themes.options[0].value
        : this.themes.options[1].value;
    },
    getEffectiveWaves() {
      return this.wavesSelected.length === 0 
        ? this.waves 
        : this.wavesSelected.map(x => ({ id: x, label: x }));
    }
  },

  methods: {
    // Markets
    computeMarketQuestions(data) {
      return data.Question.map(q => {
        const country = data.Countries.find(c => c.CountryId === q.CountryId);
        return {
            ...q, CountryName: country ? country.CountryName : undefined
        };
      });
    },

    initMarketColumns(waves, clusters, clusterGroups) {
      const results = this.getEffectiveWaves.map(w => { 
        const wave = w.id;
        return {
          field: wave,
          headerName: wave,
          minWidth: 120,
          resizable: true,
          openByDefault: true,
          children: [
            {
              field: wave,
              headerName: wave,
              width: 120,
              maxWidth: 120,
              resizable: true,
              sortable: false,
              filter: true,
              comparator: (valueA, valueB, nodeA, nodeB) => {
                let strA = valueA ? valueA.toString() : '';
                let strB = valueB ? valueB.toString() : '';
                // Check group levels and sort accordingly
                if (nodeA.level === nodeB.level) {
                  if (nodeA.level === 0) { 
                    let c1 = clusterGroups.find(x => x.CountryName === nodeA.key);
                    let c2 = clusterGroups.find(x => x.CountryName === nodeB.key);
                    if (c1 && c2) {
                      const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wave).Result);
                      const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wave).Result);
                      return c1Value < c2Value ? -1 : 1;
                    } else if (!c1 && !c2) {
                      return 0;
                    } else if (!c1) {
                      return -1;
                    } else if (!c2) {
                      return 1;
                    }
                  } else if (nodeA.level === 1) {
                    let cl1 = clusters.find(x => x.Name === nodeA.key && x.CountryId === clusterGroups.find(x => x.CountryName === nodeA.parent.key).CountryId);
                    let cl2 = clusters.find(x => x.Name === nodeB.key && x.CountryId === clusterGroups.find(x => x.CountryName === nodeB.parent.key).CountryId);
                    if (cl1 && cl2) {
                      const cl1Value = parseInt(cl1.SurveyWave.find(x => x.Name === wave).Result);
                      const cl2Value = parseInt(cl2.SurveyWave.find(x => x.Name === wave).Result);
                      return cl1Value < cl2Value ? -1 : 1;
                    } else if (!cl1 && !cl2) {
                      return 0;
                    } else if (!cl1) {
                      return -1;
                    } else if (!cl2) {
                      return 1;
                    }
                  }
                 
                  // Both nodes are at the same level
                  return strA?.localeCompare(strB);
                }
              },
              cellRenderer: (params) => {
                if (params.node.key === null) {
                  return params.value !== undefined ? `${params.value}%` : params.value;
                }

                let ctry = params.context.getCountry(params.node.key);
                if (ctry) {
                  let country = clusterGroups.find(x => x.CountryId === ctry.CountryId);
                  let wave = country?.SurveyWave.find(x => x.Name === params.colDef.field);
                  return wave ? `${wave.Result}%` : undefined;
                }

                // Level 2 Item
                ctry = params.context.getCountry(params.node.parent.key);
                let country = clusters.find(x => x.CountryId === ctry.CountryId && x.Code === params.node.key);
                let wave = country?.SurveyWave?.find(x => x.Name === params.colDef.field);

                if (wave && wave.Result === undefined) {
                  return undefined;
                }
                return wave ? `${wave.Result}%` : undefined;
              },
              valueGetter: (params) => {
                if (params.data) {
                  if (params.data.questions && params.data.questions.length === 0) {
                    return undefined;
                  }
                  const num = parseInt(params.data[wave]);
                  return num;
                }
                return params.value
              },
            },
            {
              field: wave + 'n',
              headerName: 'n',
              width: 70,
              maxWidth: 70,
              resizable: false,
              sortable: false,
              filter: true,
              columnGroupShow: 'open',
              cellRenderer: (params) => {
                if (params.node.key === null) {
                  return params.value;
                }
                
                let ctry = params.context.getCountry(params.node.key);
                if (ctry) {
                  let country = clusterGroups.find(x => x.CountryId === ctry.CountryId);
                  let wave = country?.SurveyWave.find(x => x.Name === params.colDef.field.slice(0, -1));
                  return wave ? `${wave.CheckCountTotal}` : undefined;
                }

                // Level 2 Item
                ctry = params.context.getCountry(params.node.parent.key);
                let country = clusters.find(x => x.CountryId === ctry.CountryId && x.Code === params.node.key);
                let wave = country?.SurveyWave?.find(x => x.Name === params.colDef.field.slice(0, -1));
                return wave ? `${wave.CheckCountTotal}` : undefined;
              }
            }
          ]
        }
      });

      return results;
    },

    initMarketColumnsAdditional(waves, clusters, clusterGroups) {
      const results = this.getEffectiveWaves.map(w => { 
        const wave = w.id;
        return {
          field: wave,
          headerName: wave,
          width: 120,
          hide: false,
          maxWidth: 120,
          minWidth: 120,
          resizable: true,
          sortable: true,
          filter: true,
          openByDefault: true, 
          forceVisible: true,
          comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
            const c1 = clusterGroups.find(x => x.CountryId === nodeA.data.CountryId);
            const c2 = clusterGroups.find(x => x.CountryId === nodeB.data.CountryId);
            if (c1 && c2) {
              const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wave).Result);
              const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wave).Result);
              return c1Value < c2Value ? -1 : 1;
            } else if (!c1 && !c2) {
              return 0;
            } else if (!c1) {
              return -1;
            } else if (!c2) {
              return 1;
            }
          },
          cellRenderer: (params) => {
            if (!params.data) {
              return params.value !== undefined ? `${params.value}%` : params.value;
            }
            let ctry = params.context.getCountryById(params.data.CountryId);
            if (!ctry) return undefined;
            let country = clusterGroups.find(x => x.CountryId === ctry.CountryId);
            let wave = country?.SurveyWave.find(x => x.Name === params.colDef.field);
            return wave ? `${wave.Result}%` : undefined;
          }
        }
      });

      return results;
    },

    initMarketColumnsAlternative(data) {
      const questions = this.computeMarketQuestions(data);
      const clusters = this.computeClusterQuestions(data, questions);
      const waves = this.getEffectiveWaves;

      data.Countries.forEach(x => {
        x.questions = questions.filter(y => y.CountryId === x.CountryId);
      });

      const result = this.getColumns(clusters, waves, data);
      const marketColumns = this.initMarketColumnsAdditional(waves, clusters, data.ClusterGroup);
      return [...marketColumns, ...result];
    },

    initMarketStaticColumnDefinitions() {
      return [{ 
        field: 'CountryName',
        rowGroup: true, 
        hide: true,
        minWidth: 450,
        flex: 1,
        lockVisible: true,
        // sort: 'asc',
        cellRenderer: 'MarketCellRenderer',
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
          let strA = valueA ? valueA.toString() : '';
          let strB = valueB ? valueB.toString() : '';
          return strA?.localeCompare(strB, 'de', { sensitivity: 'base' });
        },
        
      }, ...this.sharedColumnDefinitions()];
    },

    initMarketStaticColumnDefinitionsAlternative() {
      return [
        { 
          field: 'CountryName',
          rowGroup: false, 
          hide: false,
          minWidth: 200,
          flex: 1,
          lockVisible: true,
          cellRenderer: 'MarketCellRenderer',
          comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
            const strA = valueA ? valueA.toString() : '';
            const strB = valueB ? valueB.toString() : '';
            return strA?.localeCompare(strB, 'de', { sensitivity: 'base' });
          },
        }, { 
          field: 'Code',  
          rowGroup: false, 
          hide: true,
          width: 200,
          lockVisible: true,
          cellRenderer: (params) => {
            return `<span>${params.value}</span>`;
          }
        }
      ];
    },

    // Retailers
    computeRetailers(data) {
      let additionalLocations = [];
      
      const ids = [...new Set(data.Question.map(x => x.ClientLocationId))];
      for (const location of data.Locations || []) {
        ids.find(x => x === location.ClientLocationId) || additionalLocations.push(location);
      }

      let items = [ ...data.Question, ...additionalLocations ];
      for (const item of items) {
        item.nameId = item.Name + `___${item.ClientLocationId}`;
      }
      return items;
    },

    computeRetailerQuestions(data) {
      return data.Question.map(q => {
        const location = data.Locations.find(c => c.ClientLocationId === q.ClientLocationId);
        return {
            ...q, CountryName: country ? country.CountryName : undefined
        };
      });
    },

    computeRetailersAlternative(data) {
      for (const location of data.Locations || []) {
        location.nameId = location.Name + `___${location.ClientLocationId}`;
      }

      return data.Locations;
    },

    initRetailerColumns(waves, clusters, clusterGroups) {
      return this.getEffectiveWaves.map(w => {
        const wave = w.id;
        return {
          field: wave,
          headerName: wave,
          resizable: true,
          maxWidth: 120,
          minWidth: 120,
          openByDefault: true,
          sortable: false,
          filter: true,
          children: [
            {
              field: wave,
              headerName: wave,
              width: 120,
              maxWidth: 120,
              minWidth: 100,
              sortable: false,
              filter: true,
              resizable: false,
              comparator: (valueA, valueB, nodeA, nodeB) => {
                let strA = valueA ? valueA.toString() : '';
                let strB = valueB ? valueB.toString() : '';
                // Check group levels and sort accordingly
                if (nodeA.level === nodeB.level) {
                  if (nodeA.level === 0) { 
                    const regex = /___(\d+)$/;
                    let number1 = nodeA.key.match(regex) ? nodeA.key.match(regex)[1] : undefined;
                    let number2 = nodeB.key.match(regex) ? nodeB.key.match(regex)[1] : undefined;
                    let c1 = clusterGroups.find(x => x.ClientLocationId == number1);
                    let c2 = clusterGroups.find(x => x.ClientLocationId == number2);
                    if (c1 && c2) {
                      const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wave).Result);
                      const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wave).Result);
                      return c1Value < c2Value ? -1 : 1;
                    } else if (!c1 && !c2) {
                      return 0;
                    } else if (!c1) {
                      return -1;
                    } else if (!c2) {
                      return 1;
                    }
                  } else if (nodeA.level === 1) {
                    const regex = /___(\d+)$/;
                    let number1 = nodeA.parent.key.match(regex) ? nodeA.parent.key.match(regex)[1] : undefined;
                    let number2 = nodeB.parent.key.match(regex) ? nodeB.parent.key.match(regex)[1] : undefined;
                    let cl1 = clusters.find(x => x.Name === nodeA.key && x.ClientLocationId == number1);
                    let cl2 = clusters.find(x => x.Name === nodeB.key && x.ClientLocationId == number2);
                    if (cl1 && cl2) {
                      const cl1Value = parseInt(cl1.SurveyWave.find(x => x.Name === wave).Result);
                      const cl2Value = parseInt(cl2.SurveyWave.find(x => x.Name === wave).Result);
                      return cl1Value < cl2Value ? -1 : 1;
                    } else if (!cl1 && !cl2) {
                      return 0;
                    } else if (!cl1) {
                      return -1;
                    } else if (!cl2) {
                      return 1;
                    }
                  }
                  // Both nodes are at the same level
                  return strA?.localeCompare(strB, 'de', { sensitivity: 'base' });
                } else {
                  return nodeA.level < nodeB.level ? -1 : 1;
                }
              },
              cellRenderer: 'RetailerValueCellRenderer',
            },
            {
              field: wave + 'n',
              headerName: 'n',
              width: 70,
              maxWidth: 70,
              minWidth: 70,
              resizable: false,
              sortable: false,
              filter: true,
              columnGroupShow: 'open',
              cellRenderer: (params) => {
                if (params.node.key === null) {
                  return params.value;
                }
                const regex = /___(\d+)$/;

                if(!params.node.parent.key) {
                  let nodekey = params.node.key;
                  let number = nodekey && nodekey.match(regex) ? nodekey.match(regex)[1] : undefined;
                  let country = clusterGroups.find(x => x.ClientLocationId === parseInt(number));
                  let wave = country?.SurveyWave.find(x => x.Name === params.colDef.field.slice(0, -1));
                  return wave ? `${wave.CheckCountTotal}` : '';
                }

                let nodekey = params.node.parent.key;
                let number = nodekey && nodekey.match(regex) ? nodekey.match(regex)[1] : undefined;
                let country = clusters.find(x => x.ClientLocationId === parseInt(number) && x.Code === params.node.key);
                if (!country) {
                  country = clusterGroups.find(x => x.ClientLocationId === parseInt(params.node.key));
                }
                let wave = country?.SurveyWave.find(x => x.Name === params.colDef.field.slice(0, -1));
                return wave ? `${wave.CheckCountTotal}` : "";
              }
            }
          ],
          cellRenderer: (params) => {
            if (params.node.key === null) {
              return params.value !== undefined ? `${params.value}%` : params.value;
            }

            // Country
            if (params.node.key.length === 2 && /^[A-Z]+$/.test(params.node.key)) {
              let country = clusterGroups.find(x => x.CountryId === params.node.key);
              let wave = country?.SurveyWave.find(x => x.Name === params.colDef.field);
              return wave ? `${wave.Result}%` : undefined;
            } else { // Cluster
              let country = clusters.find(x => x.CountryId === params.node.parent.key && x.Code === params.node.key);
              let wave = country.SurveyWave.find(x => x.Name === params.colDef.field);
              if (wave && wave.Result === undefined) {
                return 'N/A';
              }
              return wave ? `${wave.Result}%` : undefined;
            }
          }
        }
      });
    },

    initRetailerColumnsAdditional(waves, clusters, clusterGroups) {
      return this.getEffectiveWaves.map(w => {
        const wave = w.id;
        return {
          field: wave,
          headerName: wave,
          width: 120,
          forceVisible: true,
          maxWidth: 120,
          minWidth: 120,
          resizable: false,
          sortable: true,
          filter: true,
          comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
            const c1 = clusterGroups.find(x => x.ClientLocationId === nodeA.data.ClientLocationId);
            const c2 = clusterGroups.find(x => x.ClientLocationId === nodeB.data.ClientLocationId);
            if (c1 && c2) {
              const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wave).Result);
              const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wave).Result);
              return c1Value < c2Value ? -1 : 1;
            } else if (!c1 && !c2) {
              return 0;
            } else if (!c1) {
              return -1;
            } else if (!c2) {
              return 1;
            }
          },
          cellRenderer: (params) => {
            if (!params.data) {
              return params.value !== undefined ? `${params.value}%` : params.value;
            }
            let retailer = clusterGroups.find(x => x.ClientLocationId === params.data.ClientLocationId);
            let wave = retailer?.SurveyWave.find(x => x.Name === params.colDef.field);
            return wave ? `${wave.Result}%` : undefined;
          }       
        }
      });
    },

    initRetailerColumnsAlternative(data) {
      const questions = data.Question;
      const clusters = this.computeClusterQuestions(data, questions);
      const waves = this.getEffectiveWaves;

      data.Locations.forEach(x => {
        x.questions = questions.filter(y => y.ClientLocationId === x.ClientLocationId);
      });

      const result = this.getColumns(clusters, waves, data);
      const marketColumns = this.initRetailerColumnsAdditional(waves, clusters, data.ClusterGroup);
      return [...marketColumns, ...result];
    },

    initRetailerStaticColumnDefinitions() {
      return [{
        headerName: this.$t('Webportal.CX.Retailer'),
        field: 'nameId',
        rowGroup: true, 
        lockVisible: true,
        hide: true,
        width: 450,
        minWidth: 250,
        flex: 1,
        suppressMenu: true,
        cellRenderer: 'RetailerCellRenderer',
      },
      ...this.sharedColumnDefinitions(),
      {
        headerName: this.$t('Webportal.Reports'),
        field: 'ClientLocationIdOnClientSite',
        minWidth: 130,
        maxWidth: 130,
        sortable: false,
        filter: false,
        cellRenderer: 'LastReportCellRenderer'
      },
      {
        headerName: this.$t('Webportal.CX.JLR-Number'),
        field: 'ClientLocationIdOnClientSite',
        minWidth: 180,
        maxWidth: 180,
        sortable: true,
        filter: true,
        valueGetter: (params) => {
          if (params.data)
            return params.data.ClientLocationIdOnClientSite;

          if (params.node.allLeafChildren[0]) {
            return params.node.allLeafChildren[0].data.ClientLocationIdOnClientSite;
          }
          return '';
        }
      }];
    },
    
    initRetailerStaticColumnDefinitionsAlternative() {
      return [{
        headerName: this.$t('Webportal.CX.Retailer'),
        field: 'nameId',
        rowGroup: false, 
        lockVisible: true,
        hide: false,
        width: 450,
        minWidth: 280,
        flex: 1,
        suppressMenu: true,
        sortable: true,
        filter: true,
        cellRenderer: 'RetailerCellRenderer',
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
          const strA = valueA ? valueA.toString() : '';
          const strB = valueB ? valueB.toString() : '';
          return strA?.localeCompare(strB, 'de', { sensitivity: 'base' });
        }
      }, { 
        field: 'Code',  
        rowGroup: false, 
        hide: true,
        width: 450,
        minWidth: 300,
        lockVisible: true,
        cellRenderer: (params) => {
          return `<span>${params.value}</span>`;
        }
      }, {
        field: "QuestionText",
        width: 450,
        minWidth: 450,
        rowGroup: false, 
        hide: true,
        resizable: true,
        autoHeight: true,
        lockVisible: true
      }, {
        headerName: this.$t('Webportal.Reports'),
        headerClass: 'group-header',
        field: 'ClientLocationIdOnClientSite',
        maxWidth: 130,
        minWidth: 100,
        sortable: false,
        filter: false,
        cellRenderer: 'LastReportCellRenderer'
      }, {
        headerName: this.$t('Webportal.CX.JLR-Number'),
        field: 'ClientLocationIdOnClientSite',
        maxWidth: 160,
        minWidth: 100,
        sortable: true,
        filter: true,
        cellRenderer: 'JlrNumberCellRenderer',
      }];
    },

    // Shared
    computeClusterQuestions(data, questions) {
      const clusters = [];
      Array.from(new Set(data.Cluster.map(x => x.Code))).forEach(x => {
        let cluster = { Code: x, Name: data.Cluster.find(w => w.Code === x).Name, Questions: [] };
        questions.filter(y => y.Code === x).forEach(z => {
          if (!cluster.Questions.find(w => w.Code === z.Code && w.QuestionText === z.QuestionText))
            cluster.Questions.push({ Code: z.Code, QuestionText: z.QuestionText, QuestionCode: z.QuestionCode });
        });
        clusters.push(cluster);
      });
      return clusters;
    },

    getColumns(clusters, waves, data) {
      const result = [];
      clusters.forEach(c => {
        let col = {
          field: c.Code,
          dataKey: c.Code,
          headerName: c.Name + '',
          minWidth: 200,
          resizable: true,
          openByDefault: true,
          children: []
        };
        
        const oneColChild = {
          field:  c.Code + 'Criteria Value',
          dataKey: c.Code,
          headerName: 'Criteria Value',
          minWidth: 100,
          resizable: true,
          openByDefault: true,
          columnGroupShow: undefined,
          children: []
        };

        waves.forEach(wave => {
          const wId = wave.id;
          const colWave = {
            field: c.Code + 'Criteria Value' + wave.id,
            dataKey: wave.id,
            headerName: wave.label,
            resizable: true,
            openByDefault: true,
            columnGroupShow: 'open',
            minWidth: 100,
            key: c.Code,
            sortable: true,
            filter: true,
            cellRendererParams: {
              clusters: data.Cluster,
            },
            comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
              if (data.Countries.length > 0) {
                const c1 = data.Cluster.find(x => x.CountryId === nodeA.data.CountryId && x.Code === c.Code);
                const c2 = data.Cluster.find(x => x.CountryId === nodeB.data.CountryId && x.Code === c.Code);
                if (c1 && c2) {
                  const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wId).Result);
                  const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wId).Result);
                  return c1Value < c2Value ? -1 : 1;
                } else if (!c1 && !c2) {
                  return 0;
                } else if (!c1) {
                  return -1;
                } else if (!c2) {
                  return 1;
                }
              } else {
                const c1 = data.Cluster.find(x => x.ClientLocationId === nodeA.data.ClientLocationId && x.Code === c.Code);
                const c2 = data.Cluster.find(x => x.ClientLocationId === nodeB.data.ClientLocationId && x.Code === c.Code);
                if (c1 && c2) {
                  const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wId).Result);
                  const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wId).Result);
                  return c1Value < c2Value ? -1 : 1;
                } else if (!c1 && !c2) {
                  return 0;
                } else if (!c1) {
                  return -1;
                } else if (!c2) {
                  return 1;
                }
              }
            },
            cellRenderer: params=> {
              if (!params.data) return undefined;
              if(params.data.CountryId === undefined) {
                const item = params.clusters.find(x => x.ClientLocationId === params.data.ClientLocationId && x.Code === params.colDef.key);
                if (!item) return undefined;
                let result = item.SurveyWave.find(x => x.Name === params.colDef.dataKey);
                if (!result || !result.Result) return undefined;
                return `${result.Result}%`;
              } else {
                const item = params.clusters.find(x => x.CountryId === params.data.CountryId && x.Code === params.colDef.key);
                if (!item) return undefined;
                const result = item.SurveyWave.find(x => x.Name === params.colDef.dataKey);
                if (!result || !result.Result) return undefined;
                return `${result.Result}%`;
              }
            }
          };
          oneColChild.children.push(colWave);
        });

        col.children.push(oneColChild);

        c.Questions.forEach(q => {
          const colChild = {
            hide: result.length > 0,
            field: c.Code + q.QuestionCode,
            dataKey: q.QuestionCode,
            headerName: q.QuestionText,
            minWidth: 200,
            resizable: true,
            openByDefault: true,
            columnGroupShow: 'open',
            children: []
          };

          waves.forEach(wave => {
            const colWave = {
              field: c.Code + q.QuestionCode + wave.id,
              headerName: wave.label,
              dataKey: wave.id,
              minWidth: 100,
              resizable: true,
              key: q.QuestionCode,
              sortable: true,
              filter: true,
              comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                if (data.Countries.length > 0) {
                  const c1 = data.Question.find(x => x.CountryId === nodeA.data.CountryId && x.Code === c.Code && x.QuestionCode === q.QuestionCode);
                  const c2 = data.Question.find(x => x.CountryId === nodeB.data.CountryId && x.Code === c.Code && x.QuestionCode === q.QuestionCode);
                  if (c1 && c2) {
                    const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wave.id).Result);
                    const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wave.id).Result);
                    return c1Value < c2Value ? -1 : 1;
                  } else if (!c1 && !c2) {
                    return 0;
                  } else if (!c1) {
                    return -1;
                  } else if (!c2) {
                    return 1;
                  }
                } else {
                  const c1 = data.Question.find(x => x.ClientLocationId === nodeA.data.ClientLocationId && x.Code === c.Code);
                  const c2 = data.Question.find(x => x.ClientLocationId === nodeB.data.ClientLocationId && x.Code === c.Code);
                  if (c1 && c2) {
                    const c1Value = parseInt(c1.SurveyWave.find(x => x.Name === wave.id).Result);
                    const c2Value = parseInt(c2.SurveyWave.find(x => x.Name === wave.id).Result);
                    return c1Value < c2Value ? -1 : 1;
                  } else if (!c1 && !c2) {
                    return 0;
                  } else if (!c1) {
                    return -1;
                  } else if (!c2) {
                    return 1;
                  }
                }
              },
              cellRenderer: (params) => {
                if (!params.data) { return undefined; }
                const item = params.data.questions.find(x => x.QuestionCode === params.column.colDef.key);
                if (!item || !params.column.colDef.dataKey || !item[params.column.colDef.dataKey]) { return undefined; }
                return  `${item[params.column.colDef.dataKey]}%`;
              }
              };
            colChild.children.push(colWave);
          });
          col.children.push(colChild);
        });
        
        result.push(col);
      });

      return result;
    },

    sharedColumnDefinitions() {
      return [
        { 
          field: 'Code',  
          rowGroup: true, 
          hide: true,
          minWidth: 250,
          width: 450,
          lockVisible: true,
          cellRenderer: (params) => {
            const text = params.context.getTranslation(params.node.parent.key, params.node.key);
            return `<span>${text}</span>`;
          }
        }, {
          field: "QuestionText",
          width: 450,
          minWidth: 500,
          rowGroup: false, 
          hide: true,
          resizable: true,
          autoHeight: true,
          lockVisible: true
        }
      ];
    },

    exportExcel(params) {
      this.$nextTick(() => { 
      
        let date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        let excelFileName = `${params.table}_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.xlsx`;
        
        this.gridApi.exportDataAsExcel(
          {
            author: 'MSM Loop',
            allColumns: true,
            sheetName: params.table,
            skipRowGroups: true,
            skipColumnGroupHeaders: true,
            suppressRowOutline: true,
            suppressColumnOutline:  true,
            fileName: excelFileName,
            processCellCallback(params) {
              if (params.column.colId === 'CountryId') {
                return params.node.data.CountryName;
              }

              const value = params.value
              return value === undefined ? '' : `${value}`
            },
          }
        );
      });
    },

    flattenTree(nodes) {
      let result = [];
      // Define a recursive helper function
      function recurse(currentNode) {
        if (currentNode) {
          // Add the current node to the result array
          result.push(currentNode);
          // If the current node has children, recurse on each child
          if (currentNode.children && currentNode.children.length > 0) {
            currentNode.children.forEach(child => recurse(child));
          }
        }
      }
      // Start recursion with the root node
      nodes.forEach(node => {
        recurse(node);
      });
      return result;
    },

    restoreSettings(tree, store) {
      tree.forEach(x => x.hide = true);
      if (!store.firstLoad) {
        let backup = this.flattenTree(store.columnBackup);
        backup.forEach(node => {
          let found = tree.find(x => x.field === node.colId);
          if (found) {
            found.hide = node.hide;
            found.width = node.width;
          }
        });
      }
    }
  }
}
